import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, Button, Alert, Image } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { getNativeLanguageName } from '../constants/languageNames';
import { getImageFrameStyle, getImageTagStyle } from '../constants/imageDisplay';
import CoverTextSettingsControls from '../components/CoverTextSettingsControls';

// Statuses where story generation is still in progress
const STORY_GENERATING_STATUSES = ['pending', 'story_qa'];

function AdminStoryReviewScreen() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const [job, setJob] = useState(null);
    const [pages, setPages] = useState([]);
    const [images, setImages] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);

    const authConfig = () => ({ headers: { Authorization: `Bearer ${userInfo.token}` } });

    const loadJob = () => {
        axios.get(`/api/genai/admin/jobs/${jobId}/`, authConfig())
            .then(res => setJob(res.data))
            .catch(err => setError(err.response?.data?.error || err.message));
    };

    const loadStory = () => {
        axios.get(`/api/genai/admin/jobs/${jobId}/story/`, authConfig())
            .then(res => setPages(res.data.pages || []))
            .catch(err => setError(err.response?.data?.error || err.message));
    };

    useEffect(() => {
        if (!userInfo || !userInfo.isAdmin) {
            navigate('/login');
            return;
        }
        setLoading(true);
        Promise.all([
            axios.get(`/api/genai/admin/jobs/${jobId}/`, authConfig()),
            axios.get(`/api/genai/admin/jobs/${jobId}/story/`, authConfig()),
            axios.get(`/api/genai/admin/jobs/${jobId}/images/`, authConfig()),
        ]).then(([jobRes, storyRes, imageRes]) => {
            setJob(jobRes.data);
            setPages(storyRes.data.pages || []);
            setImages(imageRes.data || {});
            setLoading(false);
        }).catch(err => {
            setError(err.response?.data?.error || err.message);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, navigate, userInfo]);

    // Poll while story is still being (re)generated
    useEffect(() => {
        if (!job || !STORY_GENERATING_STATUSES.includes(job.status)) return;
        const interval = setInterval(() => {
            loadJob();
            loadStory();
        }, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.status]);

    const handleApprove = () => {
        setActionLoading(true);
        setActionError(null);
        axios.post(`/api/genai/jobs/${jobId}/approve-story/`, {}, authConfig())
            .then(() => navigate(`/admin/genai/jobs/${jobId}/review-pages`))
            .catch(err => setActionError(err.response?.data?.error || err.message))
            .finally(() => setActionLoading(false));
    };

    const handleRequestChanges = () => {
        const feedback = window.prompt('What should change about this story?');
        if (!feedback || !feedback.trim()) return;
        setActionLoading(true);
        setActionError(null);
        axios.post(`/api/genai/jobs/${jobId}/regenerate-story/`, { feedback }, authConfig())
            .then(() => { loadJob(); loadStory(); })
            .catch(err => setActionError(err.response?.data?.error || err.message))
            .finally(() => setActionLoading(false));
    };

    if (loading) return <Loader />;
    if (error) return <Message variant="danger">{error}</Message>;
    if (!job) return null;

    const isGenerating = STORY_GENERATING_STATUSES.includes(job.status);
    const isReady = ['story_generated', 'awaiting_approval'].includes(job.status);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>Staff QA — Story</h1>
                <Link to={`/admin/genai/jobs/${jobId}`}>Back to job detail</Link>
            </div>

            <Alert variant="info">
                Customer chose "TaleTots reviews for me". Job {jobId} · Language:{' '}
                {getNativeLanguageName(job.input_data?.language || 'English', 'English')}
            </Alert>

            {actionError && <Message variant="danger">{actionError}</Message>}

            {images['0'] && (
                <Card className="mb-4 border-primary">
                    <Card.Header className="bg-primary text-white">
                        <h4 className="mb-0">Front Cover Draft</h4>
                    </Card.Header>
                    <Card.Body className="text-center">
                        <div style={getImageFrameStyle(images['0'], '0', { maxHeightCover: 520, maxHeightStory: 520 })}>
                            <Image
                                src={images['0'].url || images['0'].image_url}
                                alt="Front cover draft"
                                fluid
                                className="border rounded shadow"
                                style={getImageTagStyle()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            )}

            <CoverTextSettingsControls
                job={job}
                token={userInfo.token}
                onSaved={(updatedJob) => setJob(updatedJob)}
            />

            {isGenerating ? (
                <div className="text-center py-5 text-muted"><Loader /><p className="mt-3">Story is being generated…</p></div>
            ) : (
                <>
                    {pages.map((page, index) => {
                        const pageNum = page.page_number ?? index;
                        const label = pageNum === 0 ? 'Front Cover' : pageNum === 13 ? 'Back Cover' : `Page ${pageNum}`;
                        const text = (page.text || page.text_on_page || '').replace(/\*\*/g, '');
                        const pic = page.picture_translated || page.picture || page.picture_description || '';
                        return (
                            <div key={index} className={index < pages.length - 1 ? 'mb-4 pb-4 border-bottom' : 'mb-2'}>
                                <div className="fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>{label}</div>
                                <p className="ps-2 border-start border-3 border-info text-secondary" style={{ fontSize: '0.9rem' }}>{pic || <em>None</em>}</p>
                                <p className="ps-2 border-start border-3 border-success" style={{ fontSize: '0.95rem' }}>{text || <em>No text</em>}</p>
                            </div>
                        );
                    })}

                    {isReady && (
                        <Card className="mb-4 p-3 bg-light">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="mb-0">Approve this story on the customer's behalf?</h5>
                                <div className="d-flex gap-2">
                                    <Button variant="outline-secondary" onClick={handleRequestChanges} disabled={actionLoading}>
                                        Request changes
                                    </Button>
                                    <Button variant="success" onClick={handleApprove} disabled={actionLoading || !pages.length}>
                                        {actionLoading ? 'Approving…' : 'Approve Story'}
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}

export default AdminStoryReviewScreen;
